import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Billing } from "@smash-sdk/billing/01-2022";
import { AsyncState } from '../../shared/models/async.interface';
import { RootState } from '../../store';
import { Plan } from '../planUpgrade/models/Plan.interface';

interface PaymentMethodDisplay {
    last4: string;
    expiryDate: string;
    type: string;
}

interface Display extends AsyncState {
    data: PaymentMethodDisplay | null
}

interface PaymentMethodUpdate extends AsyncState {
    paymentStatus: string, // FIXME Type
    userAction: 'purchase' | 'updatePaymentMethod';
    token: string | null;
    secret: string | null;
    stripeError: string | null;
}

export interface PaymentMethodState {
    display: Display;
    payment: PaymentMethodUpdate;
}

export const getPaymentMethod = createAsyncThunk(
    'paymentMethod/getPaymentMethod',
    async (_, thunkApi) => {
        try {
            const billing = new Billing();
            const { paymentMethod: { type, last4, expiryDate } } = await billing.getStripePaymentMethod();
            return { last4: last4.toString(), expiryDate, type };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
);

export const setUserAction = createAsyncThunk(
    'paymentMethod/setUserAction',
    async (userAction: PaymentMethodUpdate['userAction'], thunkApi) => {
        return { userAction };
    }
)

export const updatePaymentStatus = createAsyncThunk(
    'paymentMethod/updatePaymentStatus',
    async ({ status, token, stripeError }: { status: string, token?: string, stripeError?: string }, thunkApi) => {
        return { paymentStatus: status, token, stripeError };
    }
)

export const createStripeSetupIntent = createAsyncThunk(
    'paymentMethod/createStripeSetupIntent',
    async ({ token }: { token: string }, thunkApi) => {
        try {
            const billing = new Billing();
            const { setupIntent: { clientSecret } } = await billing.createStripeSetupIntent();
            return { paymentStatus: 'confirmSetupIntent', secret: clientSecret, token };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
);

export const prepareStripePayment = createAsyncThunk(
    'planUpgrade/prepareStripePayment',
    async ({ cardToken, selectedPlan }: { cardToken: string, selectedPlan: Plan }, thunkApi) => {
        try {
            const billing = new Billing();
            await billing.createSubscription({ plan: selectedPlan.id });
            const { paymentIntent: { clientSecret } } = await billing.initiateStripePayment();
            return { paymentStatus: 'confirmCardPayment', secret: clientSecret, token: cardToken };
        } catch (error) {
            throw error;
        }
    }
)

export const executeStripePayment = createAsyncThunk(
    'planUpgrade/executeStripePayment',
    async ({ paymentIntent }: any, thunkApi) => {
        try {
            const billing = new Billing();
            await billing.executeStripePayment({ paymentIntentId: paymentIntent.id });
            return { paymentStatus: 'paymentSucceeded' };
        } catch (error) {
            throw error;
        }
    }
)

export const initialState: PaymentMethodState = {
    display: {
        data: null,
        isLoading: false,
        isError: false,
        isSuccess: false
    },
    payment: {
        userAction: 'updatePaymentMethod',
        paymentStatus: 'waiting',
        secret: null,
        token: null,
        stripeError: null,
        isLoading: false,
        isSuccess: false,
        isError: false,
    }
}

export const paymentMethodSlice = createSlice({
    name: 'paymentMethod',
    initialState,
    reducers: {
        resetPaymentMethod: (state) => ({ ...state, ...initialState })
    },
    extraReducers: (builder) => {
        builder
            .addCase(setUserAction.fulfilled, (state, action) => {
                state.payment.userAction = action.payload.userAction;
            })

            .addCase(getPaymentMethod.pending, (state) => {
                state.display.isLoading = true;
                state.display.isError = false;
            })
            .addCase(getPaymentMethod.fulfilled, (state, action) => {
                state.display.isLoading = false;
                state.display.isSuccess = true;
                state.display.data = action.payload;
            })
            .addCase(getPaymentMethod.rejected, (state, action) => {
                state.display.isLoading = false;
                state.display.isSuccess = false;
                state.display.isError = true;
            })
            .addCase(updatePaymentStatus.pending, (state, action) => {
                state.payment.isLoading = true;
                state.payment.isSuccess = false;
                state.payment.isError = false;
            })
            .addCase(updatePaymentStatus.fulfilled, (state, action) => {
                state.payment.isLoading = false;
                state.payment.isSuccess = true;
                state.payment.isError = false;
                state.payment.paymentStatus = action.payload.paymentStatus;
                if (action.payload.token) {
                    state.payment.token = action.payload.token;
                }
                if (action.payload.stripeError) {
                    state.payment.stripeError = action.payload.stripeError;
                }
            })
            .addCase(createStripeSetupIntent.pending, (state) => {
                state.payment.isLoading = true;
                state.payment.isSuccess = false;
                state.payment.isError = false;
            })
            .addCase(createStripeSetupIntent.fulfilled, (state, action) => {
                state.payment.isLoading = false;
                state.payment.isSuccess = true;
                state.payment.isError = false;
                state.payment.paymentStatus = action.payload.paymentStatus;
                state.payment.token = action.payload.token;
                state.payment.secret = action.payload.secret;
            })
            .addCase(createStripeSetupIntent.rejected, (state) => {
                state.payment.isSuccess = false;
                state.payment.isLoading = false;
                state.payment.isError = true;
                state.payment.paymentStatus = initialState.payment.paymentStatus;
            })

            .addCase(prepareStripePayment.pending, (state) => {
                state.payment.isLoading = true;
                state.payment.isSuccess = false;
                state.payment.isError = false;
            })
            .addCase(prepareStripePayment.fulfilled, (state, action) => {
                state.payment.isLoading = false;
                state.payment.isSuccess = true;
                state.payment.isError = false;
                state.payment.paymentStatus = action.payload.paymentStatus;
                state.payment.token = action.payload.token;
                state.payment.secret = action.payload.secret;
            })
            .addCase(prepareStripePayment.rejected, (state) => {
                state.payment.isSuccess = false;
                state.payment.isLoading = false;
                state.payment.isError = true;
                state.payment.paymentStatus = initialState.payment.paymentStatus;
            })

            .addCase(executeStripePayment.pending, (state) => {
                state.payment.isSuccess = false;
                state.payment.isLoading = true;
                state.payment.isError = false;
            })
            .addCase(executeStripePayment.fulfilled, (state, action) => {
                state.payment.isSuccess = true;
                state.payment.isLoading = false;
                state.payment.isError = false;
                state.payment.paymentStatus = action.payload.paymentStatus;
            })
            .addCase(executeStripePayment.rejected, (state) => {
                state.payment.isSuccess = false;
                state.payment.isLoading = false;
                state.payment.isError = true;
                state.payment.paymentStatus = 'paymentFailed';
            })
    }
});

export const { resetPaymentMethod } = paymentMethodSlice.actions;

export const paymentMethodState = (state: RootState) => state.paymentMethod;

export default paymentMethodSlice.reducer;